import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GeneralService, LanguageService, LanguageSearchResponse, CreateUpdateLanguagePayload } from '@gs-2.0/core';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { DropdownItem } from '../dropdown/dropdown.component';

@Component({
  selector: 'app-add-language-dialog',
  standalone: false,
  templateUrl: './add-language-dialog.component.html',
  styleUrls: ['./add-language-dialog.component.scss']
})
export class AddLanguageDialogComponent implements OnInit, OnDestroy {
  /**
   * Available languages that can be added (not yet added to the system)
   */
  availableLanguages: DropdownItem[] = [];

  /**
   * Currently selected language
   */
  selectedLanguage: DropdownItem | null = null;

  /**
   * Whether the dropdown is open
   */
  isDropdownOpen = false;

  /**
   * Search keyword for language search
   */
  searchKeyword = '';

  /**
   * Subject for search input changes
   */
  private searchSubject$ = new Subject<string>();

  /**
   * Whether a search is in progress
   */
  isSearching = false;

  /**
   * Whether a language creation is in progress
   */
  isCreating = false;

  /**
   * Subject for unsubscribing
   */
  private destroy$ = new Subject<void>();

  private generalService = inject(GeneralService);
  private languageService = inject(LanguageService);

  constructor(
    public dialogRef: MatDialogRef<AddLanguageDialogComponent>
  ) {}

  /**
   * Set up search subscription with debounce
   */
  private setupSearch(): void {
    this.searchSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(keyword => {
        if (keyword) {
          this.searchLanguages(keyword.trim().toLowerCase());
        } else {
          // Clear results when search is empty
          this.availableLanguages = [];
          this.selectedLanguage = null;
          this.isSearching = false;
        }
      });
  }

  ngOnInit(): void {
    this.setupSearch();
  }

  /**
   * Search for languages using the API
   */
  private searchLanguages(keyword: string): void {
    this.isSearching = true;

    this.languageService
      .searchLanguages(keyword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (searchResponse: LanguageSearchResponse) => {
          const languages: DropdownItem[] = [];
          const existingCodes = this.generalService.languages.map(lang => lang.code);

          // Convert API response to dropdown items
          Object.keys(searchResponse.response).forEach(langCode => {
            const langData = searchResponse.response[langCode];
            if (langData && langData.length > 0 && !existingCodes.includes(langCode)) {
              // Use the first entry for each language code
              const firstEntry = langData[0];
              languages.push({
                id: langCode,
                label: `${firstEntry.language} (${firstEntry.country})`,
                data: {
                  flag: firstEntry.flag,
                  country: firstEntry.country
                }
              });
            }
          });

          this.availableLanguages = languages;
          this.isSearching = false;

          // Auto-select first result if available
          if (this.availableLanguages.length > 0) {
            this.selectedLanguage = this.availableLanguages[0];
          } else {
            this.selectedLanguage = null;
          }
        },
        error: () => {
          this.isSearching = false;
          this.availableLanguages = [];
          this.selectedLanguage = null;
        }
      });
  }

  /**
   * Handle search input changes
   */
  onSearchChange(value: string): void {
    this.searchKeyword = value;
    this.searchSubject$.next(value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle language selection from dropdown
   */
  onLanguageSelected(item: DropdownItem): void {
    this.selectedLanguage = item;
    this.isDropdownOpen = false;
    // Update search field to show selected language
    this.searchKeyword = item.label;
  }

  /**
   * Close dropdown
   */
  onDropdownClosed(): void {
    this.isDropdownOpen = false;
  }

  /**
   * Handle search input focus
   */
  onSearchFocus(): void {
    this.isDropdownOpen = true;
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Handle start translating button click
   */
  onStartTranslating(): void {
    if (this.selectedLanguage && !this.isCreating) {
      this.isCreating = true;

      const payload: CreateUpdateLanguagePayload = {
        countryName: this.selectedLanguage.data?.country || '',
        languageCode: this.selectedLanguage.id,
        state: 'DRAFT',
        uuid: null
      };

      this.languageService
        .createUpdateLanguage(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.isCreating = false;
            this.dialogRef.close({
              languageCode: this.selectedLanguage!.id,
              countryName: this.selectedLanguage!.data?.country,
              action: 'start-translating',
              success: true,
              response
            });
          },
          error: (error) => {
            this.isCreating = false;
            console.error('Failed to create language:', error);
            this.dialogRef.close({
              languageCode: this.selectedLanguage!.id,
              action: 'start-translating',
              success: false,
              error
            });
          }
        });
    }
  }
}
